/**
 * SimulatePage
 * 模拟交易主页面（专业交易终端布局）
 * 参照 npage/crypt-trade-gemini.html 重构
 *
 * @author page
 * @date 2026-04-23
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {App, Form, InputNumber, Modal, Select, Spin, Table, Tag, Tooltip, Typography} from 'antd';
import {InfoCircleOutlined, SettingOutlined} from '@ant-design/icons';
import type {
    IndicatorConfigMap,
    IndicatorInstanceConfig,
    SimulateAccount,
    SimulateLedger,
    SimulatePosition,
    SymbolConfigRecord
} from '../../types/simulate';
import {getNextIndicatorColor} from '../../types/simulate';
import {simulateSymbolConfigService, simulateTradingService} from '../../services/simulateService';
import OrderTable from '../../components/simulate/OrderTable';
import AssetOverview from '../../components/simulate/AssetOverview';
import {
    KlineChartPanel,
    OrderBook,
    PositionTable,
    TradeHistoryTable,
    TradePanel,
    TradingFooter,
} from '../../components/simulate';
import type {OhlcData} from '../../components/simulate/KlineChartPanel';
import {
    dedupInstancePeriods,
    fillMissingColors,
    getDefaultInstanceForType,
    getDefaultInstances,
    getStorageKey
} from '../../components/simulate/KlineChartPanel';
import styles from '../../components/simulate/KlineChartPanel.module.css';
import './SimulatePage.css';

const {Text} = Typography;

const MODES = [
    {label: '现货', value: 0},
    {label: '合约', value: 1},
    {label: '混合', value: 2},
];

/** 标签页类型 */
type TabType = 'positions' | 'orders' | 'history' | 'ledger' | 'assets';

/** 交易模式类型 */
type ModeType = 'spot' | 'futures';

const SimulatePage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const [account, setAccount] = useState<SimulateAccount | null>(null);
    const [loading, setLoading] = useState(true);
    const [initVisible, setInitVisible] = useState(false);
    const [configVisible, setConfigVisible] = useState(false);
    const [initForm] = Form.useForm();
    const [configForm] = Form.useForm();
    const [selectedPair, setSelectedPair] = useState('ETH/USDT');
    const [tradingMode, setTradingMode] = useState<ModeType>('spot');
    const [activeTab, setActiveTab] = useState<TabType>('positions');
    const [ohlcData, setOhlcData] = useState<OhlcData | null>(null);
    const [positions, setPositions] = useState<SimulatePosition[]>([]);
    const [rechargeVisible, setRechargeVisible] = useState(false);
    const [rechargeAmount, setRechargeAmount] = useState(100);
    const [orderCount, setOrderCount] = useState(0);
    const [positionsRefreshKey, setPositionsRefreshKey] = useState(0);
    // 品种选择器状态
    const [showPairDropdown, setShowPairDropdown] = useState(false);
    const [availablePairs, setAvailablePairs] = useState<SymbolConfigRecord[]>([]);
    // 基于当前OHLC数据生成动态订单簿
    const [dynamicBids, setDynamicBids] = useState<{ price: string; quantity: string }[]>([]);
    const [dynamicAsks, setDynamicAsks] = useState<{ price: string; quantity: string }[]>([]);
    const [midPrice, setMidPrice] = useState('3,450.12');
    // 当前品种已有活跃持仓的杠杆（有值时锁定交易面板杠杆不可调）
    const activePositionLeverage = React.useMemo(() => {
        const activePos = positions.find(p => p.symbol === selectedPair && 0 === p.status && 1 === p.mode);
        return activePos?.leverage ?? null;
    }, [positions, selectedPair]);

    /** ============ 时间周期 & 指标状态（从 KlineChartPanel 提升上来） ============ */
    /** 时间周期 */
    const [timeframe, setTimeframe] = useState('1h');
    /** 指标实例配置 */
    const [indicatorInstances, setIndicatorInstances] = useState<IndicatorConfigMap>(() => {
        try {
            const saved = localStorage.getItem(getStorageKey(selectedPair));
            if (null !== saved) {
                const parsed = JSON.parse(saved) as IndicatorConfigMap;
                Object.keys(parsed).forEach(type => {
                    parsed[type] = (parsed[type] || []).map(dedupInstancePeriods);
                });
                return fillMissingColors(parsed);
            }
        } catch (e) {
        }
        return getDefaultInstances();
    });
    /** 指标类型选择器是否显示 */
    const [showIndicatorPicker, setShowIndicatorPicker] = useState(false);
    /** 指标选择器容器 ref（用于外部点击关闭） */
    const pickerRef = useRef<HTMLDivElement>(null);
    /** 展开的指标类型（null 表示无展开） */
    const [expandedIndType, setExpandedIndType] = useState<string | null>(null);
    /** 展开面板所属的 indicatorWrap ref */
    const expandedWrapRef = useRef<HTMLDivElement | null>(null);

    /** 指标类型分组 */
    const INDICATOR_GROUPS: { section: string; types: string[] }[] = [
        {section: '主图指标', types: ['EMA', 'SMA', 'BOLL']},
        {section: '副图指标', types: ['RSI', 'MACD', 'KDJ', 'CCI', 'ATR', 'OBV', 'ADX']},
    ];

    /** 活跃指标标签列表 */
    const activeIndicatorTags = useMemo(() => {
        const tags: { type: string; color: string; count: number; hasEnabled: boolean }[] = [];
        Object.entries(indicatorInstances).forEach(([type, instances]) => {
            if (0 === instances.length) return;
            const enabled = instances.filter(inst => inst.enabled);
            const firstInst = instances.find(inst => inst.color) || instances[0];
            tags.push({
                type,
                color: firstInst.color || '#3d5469',
                count: enabled.length,
                hasEnabled: enabled.length > 0,
            });
        });
        return tags;
    }, [indicatorInstances]);

    /** 已添加的指标类型数量 */
    const totalIndicatorTypeCount = useMemo(() => {
        let count = 0;
        Object.values(indicatorInstances).forEach(instances => {
            if (instances.length > 0) count++;
        });
        return count;
    }, [indicatorInstances]);

    /**
     * 切换指标类型是否在 toolbar 展示（通过 + 按钮选择器）
     */
    const handleToggleIndicatorType = (type: string) => {
        const instances = indicatorInstances[type] || [];
        if (instances.length > 0) {
            // 已有实例 -> 全部移除
            setIndicatorInstances(prev => {
                const next = {...prev};
                delete next[type];
                return next;
            });
        } else {
            // 无实例 -> 添加一个默认启用实例
            const newInst = getDefaultInstanceForType(type);
            newInst.color = getNextIndicatorColor(type, []);
            setIndicatorInstances(prev => ({
                ...prev,
                [type]: [newInst],
            }));
        }
    };

    /**
     * 切换 toolbar 上某个指标按钮的激活/非激活状态
     */
    const handleToggleTagActive = (type: string) => {
        const instances = indicatorInstances[type] || [];
        const hasEnabled = instances.some(inst => inst.enabled);
        if (hasEnabled) {
            // 当前有启用 -> 全部禁用
            setIndicatorInstances(prev => ({
                ...prev,
                [type]: (prev[type] || []).map(inst => ({...inst, enabled: false})),
            }));
        } else {
            // 当前全禁用 -> 启用第一个实例
            setIndicatorInstances(prev => ({
                ...prev,
                [type]: (prev[type] || []).map((inst, idx) => 0 === idx ? {...inst, enabled: true} : inst),
            }));
        }
    };

    /** 获取指标类型所属分组名称 */
    const getSectionForType = (type: string): string => {
        for (const group of INDICATOR_GROUPS) {
            if (group.types.includes(type)) return group.section;
        }
        return '';
    };

    // 指标选择器外部点击关闭
    useEffect(() => {
        if (!showIndicatorPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setShowIndicatorPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showIndicatorPicker]);

    // 展开面板外部点击关闭
    useEffect(() => {
        if (!expandedIndType) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (expandedWrapRef.current && !expandedWrapRef.current.contains(e.target as Node)) {
                setExpandedIndType(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [expandedIndType]);

    const loadAccount = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res = await simulateTradingService.getAccount();
            const acc = res.data?.data;
            if (acc) {
                setAccount(acc);
            } else if (!silent) {
                setInitVisible(true);
            }
        } catch {
            if (!silent) setInitVisible(true);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const loadPositions = async () => {
        try {
            const res = await simulateTradingService.getPositions();
            setPositions(res.data?.data || []);
        } catch {
            // ignore
        }
    };

    const [ledger, setLedger] = useState<SimulateLedger[]>([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [ledgerFilter, setLedgerFilter] = useState<number | undefined>(undefined);

    const loadLedger = async () => {
        try {
            setLedgerLoading(true);
            const res = await simulateTradingService.getLedger(ledgerFilter);
            setLedger(res.data?.data || []);
        } catch {
            // ignore
        } finally {
            setLedgerLoading(false);
        }
    };

    useEffect(() => {
        loadLedger();
    }, [ledgerFilter]);

    useEffect(() => {
        loadAccount();
        loadPositions();
    }, []);

    // 每分钟静默刷新账户总权益和持仓盈亏
    useEffect(() => {
        const timer = setInterval(() => {
            loadAccount(true);
            loadPositions();
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // 基于OHLC数据动态生成订单簿
    useEffect(() => {
        if (ohlcData && ohlcData.latestKline && ohlcData.latestKline.close) {
            const basePrice = ohlcData.latestKline.close;
            const fmtPrice = basePrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
            setMidPrice(fmtPrice);

            // 生成5档买卖单
            const newBids = Array.from({length: 5}, (_, i) => {
                const offset = 1 + i * 0.5;
                const price = basePrice * (1 - offset / 100 * (1 + Math.random() * 0.5));
                const qty = (0.05 + Math.random() * 0.2);
                return {
                    price: price.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
                    quantity: qty.toFixed(4),
                };
            });
            const newAsks = Array.from({length: 5}, (_, i) => {
                const offset = 1 + i * 0.5;
                const price = basePrice * (1 + offset / 100 * (1 + Math.random() * 0.5));
                const qty = (0.05 + Math.random() * 0.2);
                return {
                    price: price.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}),
                    quantity: qty.toFixed(4),
                };
            });
            setDynamicBids(newBids);
            setDynamicAsks(newAsks);
        }
    }, [ohlcData]);

    // 持久化指标配置到 localStorage
    useEffect(() => {
        try {
            localStorage.setItem(getStorageKey(selectedPair), JSON.stringify(indicatorInstances));
        } catch (e) {
        }
    }, [indicatorInstances, selectedPair]);

    // 品种切换时加载对应配置
    useEffect(() => {
        try {
            const saved = localStorage.getItem(getStorageKey(selectedPair));
            if (null !== saved) {
                const parsed = JSON.parse(saved) as IndicatorConfigMap;
                Object.keys(parsed).forEach(type => {
                    parsed[type] = (parsed[type] || []).map(dedupInstancePeriods);
                });
                setIndicatorInstances(fillMissingColors(parsed));
            } else {
                setIndicatorInstances(getDefaultInstances());
            }
        } catch (e) {
            setIndicatorInstances(getDefaultInstances());
        }
    }, [selectedPair]);

    // 指标选择器外部点击关闭
    useEffect(() => {
        if (!showIndicatorPicker) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setShowIndicatorPicker(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showIndicatorPicker]);

    // 展开面板外部点击关闭
    useEffect(() => {
        if (!expandedIndType) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (expandedWrapRef.current && !expandedWrapRef.current.contains(e.target as Node)) {
                setExpandedIndType(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [expandedIndType]);

    const handleInit = async () => {
        try {
            const values = await initForm.validateFields();
            await simulateTradingService.initAccount(
                values.initialBalance.toString(),
                values.mode,
            );
            messageApi.success('账户初始化成功');
            setInitVisible(false);
            loadAccount();
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '初始化失败');
        }
    };

    const handleUpdateConfig = async () => {
        try {
            const values = await configForm.validateFields();
            const update: Partial<SimulateAccount> = {};
            if (values.runStartTime) update.runStartTime = values.runStartTime;
            if (values.runEndTime) update.runEndTime = values.runEndTime;
            if (values.mode !== undefined) update.mode = values.mode;
            await simulateTradingService.updateAccount(update);
            messageApi.success('配置更新成功');
            setConfigVisible(false);
            loadAccount(true);
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '更新失败');
        }
    };

    const openConfig = () => {
        if (!account) return;
        const modeMap: Record<string, number> = {spot: 0, futures: 1};
        configForm.setFieldsValue({
            mode: modeMap[tradingMode] ?? account.mode,
            runStartTime: account.runStartTime,
            runEndTime: account.runEndTime,
        });
        setConfigVisible(true);
    };

    const handleRecharge = () => {
        setRechargeVisible(true);
    };

    /** 加载品种列表 */
    useEffect(() => {
        const loadSymbols = async () => {
            try {
                const productType = 'spot' === tradingMode ? 'spot' : 'contract';
                const response = await simulateSymbolConfigService.getByProductType(productType);
                const symbolApiResponse = response.data;
                if ('0' === symbolApiResponse.code) {
                    const symbols: SymbolConfigRecord[] = symbolApiResponse.data;
                    setAvailablePairs(symbols);
                    const uniquePairs = Array.from(new Set(symbols.map(s => s.symbol)));
                    if ('' === (selectedPair || '') || !uniquePairs.includes(selectedPair)) {
                        if (uniquePairs.length > 0) setSelectedPair(uniquePairs[0]);
                    }
                }
            } catch (error: any) {
                console.error('加载品种配置失败:', error);
                setAvailablePairs([
                    {
                        id: 1,
                        exchangeName: 'OKX',
                        productType: 'spot',
                        symbol: 'BTC/USDT',
                        enabled: 0,
                        sortOrder: 0,
                        createdAt: '',
                        updatedAt: ''
                    },
                    {
                        id: 2,
                        exchangeName: 'OKX',
                        productType: 'spot',
                        symbol: 'ETH/USDT',
                        enabled: 0,
                        sortOrder: 1,
                        createdAt: '',
                        updatedAt: ''
                    },
                    {
                        id: 3,
                        exchangeName: 'OKX',
                        productType: 'spot',
                        symbol: 'SOL/USDT',
                        enabled: 0,
                        sortOrder: 2,
                        createdAt: '',
                        updatedAt: ''
                    },
                ]);
            }
        };
        loadSymbols();
    }, [tradingMode]);

    /** 品种选择器外部点击关闭 */
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-pair-dropdown]')) setShowPairDropdown(false);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    if (loading) {
        return (
            <div className="trading-page">
                <div className="loading-container">
                    <Spin size="large"/>
                    <Text style={{marginTop: 16, display: 'block'}}>加载模拟数据中...</Text>
                </div>
            </div>
        );
    }

    // 计算账户数据
    const accountInfo = account ? {
        totalEquity: (Number(account.availableBalance) + Number(account.totalPositionValue || 0)).toFixed(2),
        todayProfit: (Number(account.unrealizedPnl || 0) >= 0 ? '+' : '') + Number(account.unrealizedPnl || 0).toFixed(2),
        todayProfitRate: '',
    } : null;

    return (
        <div className="trading-page">
            {/* 主内容区 */}
            <main className="trading-main">
                {/* 全宽工具栏（包含 Logo、模式、filter、指标、账户信息） */}
                <div className="simulateToolbar">
                    <div className={styles.toolbar}>
                        {/* 左侧：Logo + 模式切换 */}
                        <div className={styles.toolbarLeft}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                gap: 4,
                                height: '34px',
                                padding: '2px 0'
                            }}>
                                <img src="/apikeys/okx.png" alt="OKX"
                                     style={{height: '22px', width: 'auto', objectFit: 'contain'}}/>
                                <span style={{
                                    fontWeight: 'bold',
                                    fontSize: 10,
                                    letterSpacing: '-0.5px',
                                    color: 'var(--pt-primary)',
                                    lineHeight: 1,
                                    paddingBottom: 2
                                }}>模拟</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: 0,
                                background: 'transparent',
                                padding: 0,
                                borderRadius: 4,
                                border: '1px solid var(--pt-border)'
                            }}>
                                <button
                                    onClick={() => setTradingMode('spot')}
                                    style={{
                                        padding: '2px 8px', borderRadius: 0, fontSize: 11, border: 'none',
                                        borderRight: '1px solid var(--pt-border)',
                                        background: 'spot' === tradingMode ? 'rgba(59,130,246,0.12)' : 'transparent',
                                        color: 'spot' === tradingMode ? 'var(--pt-blue)' : 'var(--pt-text-dim)',
                                        fontWeight: 400,
                                        cursor: 'pointer', transition: 'all 0.12s', lineHeight: '22px',
                                        fontFamily: "'IBM Plex Mono', Monaco, monospace",
                                    }}
                                >现货
                                </button>
                                <button
                                    onClick={() => setTradingMode('futures')}
                                    style={{
                                        padding: '2px 8px', borderRadius: 0, fontSize: 11, border: 'none',
                                        background: 'futures' === tradingMode ? 'rgba(59,130,246,0.12)' : 'transparent',
                                        color: 'futures' === tradingMode ? 'var(--pt-blue)' : 'var(--pt-text-dim)',
                                        fontWeight: 400,
                                        cursor: 'pointer', transition: 'all 0.12s', lineHeight: '22px',
                                        fontFamily: "'IBM Plex Mono', Monaco, monospace",
                                    }}
                                >合约
                                </button>
                            </div>
                        </div>

                        {/* 时间周期切换 */}
                        <div className={styles.filterBar}>
                            {[
                                {label: '1m', value: '1m'},
                                {label: '5m', value: '5m'},
                                {label: '1H', value: '1h'},
                                {label: '4H', value: '4h'},
                                {label: '1D', value: '1d'},
                            ].map(item => (
                                <button
                                    key={item.value}
                                    onClick={() => setTimeframe(item.value)}
                                    className={`${styles.filterBtn} ${item.value === timeframe ? styles.filterBtnActive : ''}`}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        {/* 分隔线 */}
                        {totalIndicatorTypeCount > 0 ? <div className={styles.toolbarSep}/> : null}

                        {/* 技术指标标签 */}
                        {activeIndicatorTags.map(tag => {
                            const isExpanded = expandedIndType === tag.type;
                            return (
                                <div
                                    key={tag.type}
                                    className={styles.indicatorWrap}
                                    ref={el => {
                                        if (isExpanded) expandedWrapRef.current = el;
                                    }}
                                >
                                    <div
                                        className={`${styles.indicatorTag} ${!tag.hasEnabled ? styles.indicatorTagDim : ''}`}
                                        onClick={() => handleToggleTagActive(tag.type)}
                                    >
                                        <span className={styles.dot}
                                              style={{background: tag.hasEnabled ? tag.color : '#3d5469'}}/>
                                        {tag.type}{tag.count > 0 ? ` +${tag.count}` : ''}
                                        <span
                                            className={styles.indArrow}
                                            onClick={e => {
                                                e.stopPropagation();
                                                setExpandedIndType(prev => prev === tag.type ? null : tag.type);
                                            }}
                                        >{isExpanded ? '▴' : '▾'}</span>
                                    </div>
                                    {isExpanded && (
                                        <div className={styles.indPanel}>
                                            <div className={styles.indPanelHint}>{getSectionForType(tag.type)}</div>
                                            {(indicatorInstances[tag.type] || []).map((inst, idx) => (
                                                <div key={idx} className={styles.indPeriodRow}>
                                                <span
                                                    className={styles.indPeriodCheckbox}
                                                    onClick={() => {
                                                        setIndicatorInstances(prev => {
                                                            const list = [...(prev[tag.type] || [])];
                                                            list[idx] = {...list[idx], enabled: !list[idx].enabled};
                                                            return {...prev, [tag.type]: list};
                                                        });
                                                    }}
                                                >
                                                    {inst.enabled ?
                                                        <span className={styles.indPeriodCheck}>✓</span> : null}
                                                </span>
                                                    <span className={styles.indPeriodDot}
                                                          style={{background: inst.color || '#3d5469'}}/>
                                                    {'BOLL' === tag.type ? (
                                                        <>
                                                            <span className={styles.indPeriodLabel}>P:</span>
                                                            <InputNumber
                                                                size="small"
                                                                min={2} max={200}
                                                                value={inst.periods[0]}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        list[idx] = {...list[idx], periods: [v]};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                            <span className={styles.indPeriodLabel}>σ:</span>
                                                            <InputNumber
                                                                size="small"
                                                                min={0.5} max={5} step={0.5}
                                                                value={inst.stdDev ?? 2}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        list[idx] = {...list[idx], stdDev: v};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                        </>
                                                    ) : 'MACD' === tag.type ? (
                                                        <>
                                                            <InputNumber
                                                                size="small"
                                                                min={2} max={100}
                                                                value={inst.periods[0]}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        const periods = [...list[idx].periods];
                                                                        periods[0] = v;
                                                                        list[idx] = {...list[idx], periods};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                            <InputNumber
                                                                size="small"
                                                                min={2} max={200}
                                                                value={inst.periods[1]}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        const periods = [...list[idx].periods];
                                                                        periods[1] = v;
                                                                        list[idx] = {...list[idx], periods};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                            <InputNumber
                                                                size="small"
                                                                min={2} max={100}
                                                                value={inst.periods[2]}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        const periods = [...list[idx].periods];
                                                                        periods[2] = v;
                                                                        list[idx] = {...list[idx], periods};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                        </>
                                                    ) : (
                                                        inst.periods.map((p, pi) => (
                                                            <InputNumber
                                                                key={pi}
                                                                size="small"
                                                                min={2} max={200}
                                                                value={p}
                                                                disabled={!inst.enabled}
                                                                onChange={v => {
                                                                    if (null === v) return;
                                                                    setIndicatorInstances(prev => {
                                                                        const list = [...(prev[tag.type] || [])];
                                                                        const periods = [...list[idx].periods];
                                                                        periods[pi] = v;
                                                                        list[idx] = {...list[idx], periods};
                                                                        return {...prev, [tag.type]: list};
                                                                    });
                                                                }}
                                                                className={styles.indPeriodInput}
                                                            />
                                                        ))
                                                    )}
                                                    <span
                                                        className={styles.indPeriodDel}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setIndicatorInstances(prev => {
                                                                const list = [...(prev[tag.type] || [])];
                                                                list.splice(idx, 1);
                                                                const next = {...prev, [tag.type]: list};
                                                                if (0 === list.length) delete next[tag.type];
                                                                return next;
                                                            });
                                                        }}
                                                    >×</span>
                                                </div>
                                            ))}
                                            <div
                                                className={styles.indPeriodAdd}
                                                onClick={() => {
                                                    const existingColors = (indicatorInstances[tag.type] || [])
                                                        .filter((i: IndicatorInstanceConfig) => i.color)
                                                        .map((i: IndicatorInstanceConfig) => i.color!);
                                                    const newInst = getDefaultInstanceForType(tag.type);
                                                    newInst.color = getNextIndicatorColor(tag.type, existingColors);
                                                    setIndicatorInstances(prev => ({
                                                        ...prev,
                                                        [tag.type]: [...(prev[tag.type] || []), newInst],
                                                    }));
                                                }}
                                            >+ 添加{tag.type}实例
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* 添加指标按钮 + 选择器 */}
                        <div className={styles.addBtnWrap} ref={pickerRef}>
                            <button
                                className={styles.addBtn}
                                onClick={() => setShowIndicatorPicker(prev => !prev)}
                                title="添加技术指标"
                            >
                                +
                            </button>
                            {showIndicatorPicker && (
                                <div className={styles.pickerPanel}>
                                    {INDICATOR_GROUPS.map(group => (
                                        <div key={group.section}>
                                            <div className={styles.pickerSectionLabel}>{group.section}</div>
                                            {group.types.map(type => {
                                                const existsInToolbar = activeIndicatorTags.some(tag => tag.type === type);
                                                return (
                                                    <div
                                                        key={type}
                                                        className={styles.pickerItem}
                                                        onClick={() => handleToggleIndicatorType(type)}
                                                    >
                                                        <span
                                                            className={`${styles.pickerCheckbox} ${existsInToolbar ? styles.pickerCheckboxChecked : ''}`}>
                                                            {existsInToolbar ? '✓' : ''}
                                                        </span>
                                                        {type}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 右侧：账户信息 */}
                        <div className={styles.toolbarRight}>
                            {accountInfo && (
                                <>
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                        <span style={{
                                            fontSize: 10,
                                            color: 'var(--pt-text-secondary)'
                                        }}>总权益 (USDT)</span>
                                        <span style={{
                                            fontFamily: "'IBM Plex Mono', monospace",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: 'var(--pt-text-primary)'
                                        }}>{accountInfo.totalEquity}</span>
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                        <span style={{fontSize: 10, color: 'var(--pt-text-secondary)'}}>
                                            持仓盈亏
                                            <Tooltip title="模拟交易每分钟结算一次">
                                                <InfoCircleOutlined
                                                    style={{
                                                        marginLeft: 4,
                                                        color: 'var(--pt-text-secondary)',
                                                        fontSize: 10
                                                    }}/>
                                            </Tooltip>
                                        </span>
                                        <span style={{
                                            fontFamily: "'IBM Plex Mono', monospace",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: accountInfo.todayProfit.startsWith('-') ? 'var(--pt-bear)' : '#00c087'
                                        }}>{accountInfo.todayProfit}</span>
                                    </div>
                                </>
                            )}
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <button
                                    onClick={handleRecharge}
                                    style={{
                                        background: 'var(--pt-primary)', color: '#000', border: 'none',
                                        padding: '3px 6px', borderRadius: 4, fontSize: 13,
                                        cursor: 'pointer', lineHeight: 1, display: 'flex', alignItems: 'center',
                                    }}
                                ><SettingOutlined/></button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 中间内容区 */}
                <div className="trading-middle">
                    {/* 左侧列 - K线 + 订单簿 */}
                    <div className="chart-order-container">
                        {/* K线图表 */}
                        <div className="chart-wrapper">
                            {/* 品种选择器 + OHLC 覆盖层 */}
                            <div className="chartOverlay" data-pair-dropdown>
                                <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '2px 6px',
                                            cursor: 'pointer',
                                            color: '#fff',
                                            fontSize: 13,
                                            fontWeight: 'bold',
                                            borderRadius: 4,
                                            background: 'rgba(12,17,32,0.85)'
                                        }}
                                        onClick={() => setShowPairDropdown(!showPairDropdown)}
                                    >
                                        <span
                                            style={{whiteSpace: 'nowrap'}}>{selectedPair || (availablePairs[0]?.symbol || 'ETH/USDT')}</span>
                                        {'futures' === tradingMode && <span style={{
                                            fontSize: 10,
                                            fontWeight: 'normal',
                                            color: 'var(--pt-primary)',
                                            background: 'rgba(240,180,41,0.15)',
                                            padding: '1px 4px',
                                            borderRadius: 2
                                        }}>永续</span>}
                                        <span style={{
                                            fontSize: 9,
                                            transition: 'transform 0.2s',
                                            transform: showPairDropdown ? 'rotate(180deg)' : 'none'
                                        }}>▼</span>
                                    </div>
                                    {showPairDropdown && (
                                        <div style={{
                                            position: 'absolute', top: '100%', left: 0, marginTop: 2,
                                            background: 'var(--pt-bg-panel)', border: '1px solid var(--pt-border)',
                                            borderRadius: 4, zIndex: 100, minWidth: 130,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                        }}>
                                            {availablePairs.map(pairConfig => (
                                                <div
                                                    key={pairConfig.id}
                                                    onClick={() => {
                                                        setSelectedPair(pairConfig.symbol);
                                                        setShowPairDropdown(false);
                                                    }}
                                                    style={{
                                                        padding: '6px 10px',
                                                        cursor: 'pointer',
                                                        color: pairConfig.symbol === selectedPair ? 'var(--pt-primary)' : '#fff',
                                                        fontSize: 12,
                                                        whiteSpace: 'nowrap',
                                                        background: pairConfig.symbol === selectedPair ? 'rgba(240,180,41,0.1)' : 'transparent',
                                                    }}
                                                >
                                                    {pairConfig.symbol}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {ohlcData?.latestKline && (
                                    <div style={{
                                        display: 'flex',
                                        gap: 8,
                                        fontSize: 11,
                                        color: 'rgba(255,255,255,0.65)',
                                        background: 'rgba(12,17,32,0.85)',
                                        padding: '2px 6px',
                                        borderRadius: 4
                                    }}>
                                        <span>开: <span
                                            style={{color: '#fff'}}>{ohlcData.latestKline.open.toFixed(2)}</span></span>
                                        <span>高: <span
                                            style={{color: 'var(--pt-bull)'}}>{ohlcData.latestKline.high.toFixed(2)}</span></span>
                                        <span>低: <span
                                            style={{color: 'var(--pt-bear)'}}>{ohlcData.latestKline.low.toFixed(2)}</span></span>
                                        <span>收: <span
                                            style={{color: ohlcData.latestKline.close >= ohlcData.latestKline.open ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>{ohlcData.latestKline.close.toFixed(2)}</span></span>
                                        <span>量: <span
                                            style={{color: '#fff'}}>{ohlcData.latestKline.volume.toFixed(2)}</span></span>
                                        <span
                                            style={{color: (ohlcData.priceChange ?? 0) >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                            {(ohlcData.priceChange ?? 0) >= 0 ? '+' : ''}{ohlcData.priceChange?.toFixed(2)} ({(ohlcData.priceChangePercent ?? 0) >= 0 ? '+' : ''}{ohlcData.priceChangePercent?.toFixed(2)}%)
                                        </span>
                                    </div>
                                )}
                            </div>
                            <KlineChartPanel
                                tradingPair={selectedPair}
                                productType={'spot' === tradingMode ? 'spot' : 'contract'}
                                onPairChange={setSelectedPair}
                                onOhlcDataChange={setOhlcData}
                                timeframe={timeframe}
                                indicatorInstances={indicatorInstances}
                                onIndicatorInstancesChange={setIndicatorInstances}
                            />
                        </div>
                        {/* 订单簿 */}
                        <div className="order-wrapper">
                            <OrderBook
                                bids={dynamicBids}
                                asks={dynamicAsks}
                                midPrice={midPrice}
                            />
                        </div>
                    </div>

                    {/* 右侧交易面板 */}
                    <aside className="trade-panel-wrapper">
                        <TradePanel
                            tradingPair={selectedPair}
                            availableBalance={account?.availableBalance || '0.00'}
                            tradingMode={tradingMode}
                            positions={positions}
                            currentPrice={ohlcData?.latestKline?.close}
                            activePositionLeverage={activePositionLeverage}
                            onOrderSuccess={() => {
                                loadAccount(true);
                                loadPositions();
                                setPositionsRefreshKey(k => k + 1);
                            }}
                        />
                    </aside>
                </div>

                {/* 底部标签页和数据表格 */}
                <div className="bottom-panel">
                    <div className="tab-header">
                        <button
                            className={`tab-btn ${activeTab === 'positions' ? 'active' : ''}`}
                            onClick={() => setActiveTab('positions')}
                        >
                            当前持仓({positions.length})
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
                            onClick={() => setActiveTab('orders')}
                        >
                            当前委托({orderCount})
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            历史成交
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'ledger' ? 'active' : ''}`}
                            onClick={() => setActiveTab('ledger')}
                        >
                            资金流水
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'assets' ? 'active' : ''}`}
                            onClick={() => setActiveTab('assets')}
                        >
                            资产概览
                        </button>
                    </div>
                    <div className="tab-content">
                        {activeTab === 'positions' && <PositionTable refreshKey={positionsRefreshKey}/>}
                        {activeTab === 'orders' && (
                            <OrderTable
                                tradingPair={selectedPair}
                                onOrderUpdate={() => {
                                    loadAccount(true);
                                    loadPositions();
                                    setPositionsRefreshKey(k => k + 1);
                                }}
                                onCountChange={setOrderCount}
                            />
                        )}
                        {activeTab === 'history' && <TradeHistoryTable/>}
                        {activeTab === 'ledger' && (
                            <div style={{padding: 0, height: '100%', display: 'flex', flexDirection: 'column'}}>
                                <Table
                                    className="dark-theme-table"
                                    columns={[
                                        {
                                            title: '时间',
                                            dataIndex: 'createTime',
                                            key: 'createTime',
                                            width: 120,
                                            render: (t: string) => {
                                                const fmt = t && t.length >= 19 ? t.substring(0, 19).replace('T', ' ') : t;
                                                return <span style={{
                                                    color: 'rgba(255,255,255,0.65)',
                                                    fontSize: 12
                                                }}>{fmt || '-'}</span>;
                                            }
                                        },
                                        {
                                            title: (
                                                <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                                                    <span>事件类型</span>
                                                    <Select
                                                        value={ledgerFilter ?? undefined}
                                                        placeholder="全部"
                                                        allowClear
                                                        style={{width: 100}}
                                                        onChange={val => setLedgerFilter(val ?? undefined)}
                                                        options={[
                                                            {value: 0, label: '充值'},
                                                            {value: 2, label: '买入成交'},
                                                            {value: 3, label: '卖出成交'},
                                                        ]}
                                                        variant="borderless"
                                                        dropdownStyle={{
                                                            backgroundColor: 'var(--pt-bg-card)',
                                                            border: '1px solid var(--pt-border)'
                                                        }}
                                                        size="small"
                                                    />
                                                </div>
                                            ),
                                            dataIndex: 'eventType',
                                            key: 'eventType',
                                            width: 180,
                                            align: 'center',
                                            render: (val: number) => {
                                                const map: Record<number, { label: string; color: string }> = {
                                                    0: {label: '充值', color: 'var(--pt-bull)'},
                                                    1: {label: '提现', color: 'var(--pt-bear)'},
                                                    2: {label: '买入成交', color: 'var(--pt-bull)'},
                                                    3: {label: '卖出成交', color: 'var(--pt-bear)'},
                                                    4: {label: '资金费率', color: '#8B5CF6'},
                                                    5: {label: '手续费', color: 'var(--pt-text-secondary)'},
                                                    6: {label: '强平', color: 'var(--pt-bear)'},
                                                    7: {label: '追加保证金', color: 'var(--pt-blue)'},
                                                };
                                                const info = map[val];
                                                return info ? <Tag style={{
                                                        border: 'none',
                                                        background: `${info.color}1A`,
                                                        color: info.color,
                                                        borderRadius: 4
                                                    }}>{info.label}</Tag> :
                                                    <span style={{color: 'var(--pt-text-secondary)'}}>{val}</span>;
                                            },
                                        },
                                        {
                                            title: '金额',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            width: 120,
                                            align: 'right',
                                            render: (val: string) => {
                                                const num = Number(val);
                                                return <span style={{
                                                    color: num >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                                    fontFamily: 'Monaco, IBM Plex Mono, monospace',
                                                    fontWeight: 500
                                                }}>{num >= 0 ? '+' : ''}{val}</span>;
                                            },
                                        },
                                        {
                                            title: '余额',
                                            dataIndex: 'balanceAfter',
                                            key: 'balanceAfter',
                                            width: 120,
                                            align: 'right',
                                            render: (val: string) => <span style={{
                                                color: 'var(--pt-text-primary)',
                                                fontFamily: 'Monaco, IBM Plex Mono, monospace'
                                            }}>{val}</span>,
                                        },
                                        {
                                            title: '备注', dataIndex: 'memo', key: 'memo', width: 410,
                                            render: (t) => <span style={{
                                                color: 'rgba(255,255,255,0.45)',
                                                fontSize: 12
                                            }}>{t || '-'}</span>,
                                        },
                                    ]}
                                    dataSource={ledger}
                                    rowKey="id"
                                    pagination={false}
                                    scroll={{y: 300}}
                                    locale={{emptyText: '暂无资金流水'}}
                                    size="small"
                                    loading={ledgerLoading}
                                    onRow={() => ({style: {background: 'var(--pt-bg-panel)', color: '#fff'}})}
                                />
                            </div>
                        )}
                        {activeTab === 'assets' && account && (
                            <AssetOverview account={account}/>
                        )}
                    </div>
                </div>
            </main>

            {/* Footer */}
            <TradingFooter latency={12} serverStatus="connected"/>


            {/* 初始化弹窗 */}
            <Modal
                title={<span style={{color: '#fff'}}>初始化模拟账户</span>}
                open={initVisible}
                onOk={handleInit}
                onCancel={() => setInitVisible(false)}
                okText="初始化"
                cancelText="取消"
                className="dark-theme-modal"
                centered
            >
                <Form form={initForm} layout="vertical">
                    <Form.Item
                        name="initialBalance"
                        label={<span style={{color: '#a0a0a0'}}>初始资金 (₮)</span>}
                        initialValue={10000}
                        rules={[{required: true, message: '请输入初始资金'}]}
                    >
                        <InputNumber
                            style={{
                                width: '100%',
                                backgroundColor: 'var(--pt-bg-hover)',
                                borderColor: 'var(--pt-border)',
                                color: '#fff'
                            }}
                            min={100}
                            step={1000}
                            placeholder="请输入初始资金"
                        />
                    </Form.Item>
                    <Form.Item
                        name="mode"
                        label={<span style={{color: '#a0a0a0'}}>交易模式</span>}
                        initialValue={0}
                    >
                        <Select
                            options={MODES}
                            dropdownStyle={{
                                backgroundColor: 'var(--pt-bg-panel)',
                                border: '1px solid var(--pt-border)'
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 账户配置弹窗 */}
            <Modal
                title={<span style={{color: '#fff'}}>账户配置</span>}
                open={configVisible}
                onOk={handleUpdateConfig}
                onCancel={() => setConfigVisible(false)}
                okText="保存"
                cancelText="取消"
                className="dark-theme-modal"
                centered
            >
                <Form form={configForm} layout="vertical">
                    <Form.Item name="mode" label={<span style={{color: '#a0a0a0'}}>交易模式</span>}>
                        <Select options={MODES}/>
                    </Form.Item>
                    <Form.Item name="runStartTime" label={<span style={{color: '#a0a0a0'}}>运行开始时间</span>}>
                        <Select
                            options={Array.from({length: 24}, (_, i) => ({
                                label: `${String(i).padStart(2, '0')}:00`,
                                value: `${String(i).padStart(2, '0')}:00`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="runEndTime" label={<span style={{color: '#a0a0a0'}}>运行结束时间</span>}>
                        <Select
                            options={Array.from({length: 24}, (_, i) => ({
                                label: `${String(i).padStart(2, '0')}:59`,
                                value: `${String(i).padStart(2, '0')}:59`,
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 充值弹窗 */}
            <Modal
                title={<span style={{color: '#fff'}}>模拟充值</span>}
                open={rechargeVisible}
                onOk={async () => {
                    if (!rechargeAmount || rechargeAmount <= 0) {
                        messageApi.warning('请输入有效的充值金额');
                        return;
                    }
                    try {
                        await simulateTradingService.topUpAccount(rechargeAmount);
                        messageApi.success('充值成功');
                        setRechargeVisible(false);
                        loadAccount(true);
                    } catch (error: any) {
                        messageApi.error(error.response?.data?.message || error.message || '充值失败');
                    }
                }}
                onCancel={() => setRechargeVisible(false)}
                okText="充值"
                cancelText="取消"
                className="dark-theme-modal"
                centered
            >
                <div style={{padding: '16px 0'}}>
                    <div style={{color: 'var(--pt-text-secondary)', marginBottom: 8, fontSize: 13}}>充值金额 (₮)</div>
                    <InputNumber
                        style={{
                            width: '100%',
                            backgroundColor: 'var(--pt-bg-hover)',
                            borderColor: 'var(--pt-border)',
                            color: '#fff'
                        }}
                        min={1}
                        step={100}
                        defaultValue={100}
                        onChange={val => setRechargeAmount(val || 0)}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default SimulatePage;
